import { useCallback, useEffect, useMemo, useState } from 'react';
import { CalendarClock, MoreHorizontal } from 'lucide-react';
import { Link } from 'react-router-dom';
import {
  freshnessOf,
  lastTapPerAction,
  recentTap,
  tapAgoLabel,
  type TrackerAction,
  type TrackerMeta,
  type TrackerTap,
} from '@awwyeah/shared';
import { logTap, removeTap, useTrackerTaps } from '../hooks';
import { useHouseholdRoster } from '../useHouseholdRoster';
import { ActionPill } from './ActionPill';
import { TodayStrip } from './TodayStrip';
import { Toast } from './Toast';

/**
 * A whole tracker: its name, a row of tappable actions, and today's rail beneath them.
 *
 * Mounted in two places — the Apps tab's tracker list and, when pinned, the digest — which
 * is why it opens its own `tracker:<id>` doc rather than taking taps as a prop. Each card
 * is self-contained, so the digest can pin two trackers without the digest itself learning
 * anything about tap logs.
 *
 * The one piece of friction in the whole module lives here: `pendingConfirm`. Tapping an
 * action that somebody already tapped in the last half hour asks "…already did that N ago
 * — log anyway?" instead of writing straight away. It is a **confirmation, not a block**
 * (two people really can walk the dog an hour apart), and it is the single highest-value
 * behaviour in the feature, because double-feeding is the actual thing that goes wrong.
 */
export function TrackerCard({
  tracker,
  onOpenMenu,
  compact,
}: {
  tracker: TrackerMeta;
  /** Omitted on the digest, where a tracker is shown but not managed. */
  onOpenMenu?: () => void;
  /** Digest presentation: no card chrome, tighter, sits in the page's own rhythm. */
  compact?: boolean;
}): JSX.Element {
  const { map, taps } = useTrackerTaps(tracker.id);
  const { meId, getMember } = useHouseholdRoster();

  // A minute-resolution clock so "2h · Faye" and the freshness fade stay honest on a page
  // left open all morning, without a re-render per second.
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 60_000);
    return () => clearInterval(timer);
  }, []);

  const [toast, setToast] = useState<{ title: string; subtitle?: string; tapId: string } | null>(
    null,
  );
  const [pendingConfirm, setPendingConfirm] = useState<{
    action: TrackerAction;
    existing: TrackerTap;
  } | null>(null);

  const latest = useMemo(() => lastTapPerAction(taps), [taps]);

  const write = useCallback(
    (action: TrackerAction) => {
      if (!meId) return;
      const tap = logTap(map, action.id, meId);
      setToast({
        title: `${action.emoji ? `${action.emoji} ` : ''}${action.label} — logged`,
        subtitle: tracker.name,
        tapId: tap.id,
      });
    },
    [map, meId, tracker.name],
  );

  const onTap = useCallback(
    (action: TrackerAction) => {
      const clash = recentTap(taps, action.id, new Date());
      if (clash) setPendingConfirm({ action, existing: clash });
      else write(action);
    },
    [taps, write],
  );

  const Wrapper = compact ? 'div' : 'section';

  return (
    <Wrapper
      data-testid="tracker-card"
      data-tracker-id={tracker.id}
      className={
        compact
          ? 'flex flex-col gap-2.5'
          : 'flex flex-col gap-3 rounded-md border border-line bg-surface p-4'
      }
    >
      <div className="flex items-center gap-2">
        {/* Compact sits under the digest's own uppercase section header (`PinnedTrackers`),
            so the name stays a plain readable label rather than a second header. */}
        <h3
          className={compact ? 'text-sm font-semibold text-fg' : 'text-base font-semibold text-fg'}
        >
          {tracker.emoji ? `${tracker.emoji} ` : ''}
          {tracker.name}
        </h3>
        {/* The day view's one entry point — plan next to actual, one tracker at a time. */}
        <Link
          to={`/trackers/${tracker.id}`}
          aria-label={`${tracker.name} day view`}
          data-testid="tracker-day-link"
          className="flex h-9 w-9 items-center justify-center rounded-full text-fg-3 transition-colors hover:bg-raised"
        >
          <CalendarClock size={16} />
        </Link>
        {onOpenMenu && (
          <button
            type="button"
            onClick={onOpenMenu}
            aria-label={`${tracker.name} options`}
            data-testid="tracker-menu-button"
            className="ml-auto flex h-9 w-9 items-center justify-center rounded-full text-fg-3 transition-colors hover:bg-raised"
          >
            <MoreHorizontal size={18} />
          </button>
        )}
      </div>

      {tracker.actions.length === 0 ? (
        <p className="rounded-sm border border-dashed border-line px-3 py-2.5 text-sm text-fg-3">
          No buttons on this tracker yet.
        </p>
      ) : (
        <div className="flex flex-wrap gap-2">
          {tracker.actions.map((action) => {
            const last = latest.get(action.id) ?? null;
            return (
              <ActionPill
                key={action.id}
                action={action}
                lastTap={last}
                lastBy={last ? getMember(last.byId) : null}
                freshness={freshnessOf(last?.at ?? null, action.cadence, now)}
                agoLabel={tapAgoLabel(last?.at ?? null, now)}
                onTap={() => onTap(action)}
              />
            );
          })}
        </div>
      )}

      <TodayStrip actions={tracker.actions} taps={taps} day={now} now={now} getMember={getMember} />

      {pendingConfirm && (
        <ConfirmRepeat
          actionLabel={pendingConfirm.action.label}
          who={getMember(pendingConfirm.existing.byId).name}
          isMe={pendingConfirm.existing.byId === meId}
          ago={tapAgoLabel(pendingConfirm.existing.at, now) ?? 'just now'}
          onCancel={() => setPendingConfirm(null)}
          onConfirm={() => {
            write(pendingConfirm.action);
            setPendingConfirm(null);
          }}
        />
      )}

      {toast && (
        <Toast
          title={toast.title}
          {...(toast.subtitle ? { subtitle: toast.subtitle } : {})}
          action={{ label: 'Undo', onClick: () => removeTap(map, toast.tapId) }}
          onDismiss={() => setToast(null)}
        />
      )}
    </Wrapper>
  );
}

/**
 * The double-tap guard. Deliberately worded as information rather than an objection —
 * "Faye did that 4m ago" — with "Log it anyway" as an ordinary button, not a scary one.
 * The tracker never claims to know better than the person holding the phone.
 */
function ConfirmRepeat({
  actionLabel,
  who,
  isMe,
  ago,
  onCancel,
  onConfirm,
}: {
  actionLabel: string;
  who: string;
  isMe: boolean;
  ago: string;
  onCancel: () => void;
  onConfirm: () => void;
}): JSX.Element {
  return (
    <div
      className="flex flex-col gap-2 rounded-sm border border-line bg-raised px-3 py-2.5"
      data-testid="tracker-repeat-confirm"
    >
      <p className="text-sm text-fg-2">
        <span className="font-semibold text-fg">{isMe ? 'You' : who}</span> logged{' '}
        {/* `tapAgoLabel` already reads as a whole phrase for the freshest bucket, so the
            "ago" suffix belongs only on the durations ("20m ago", not "just now ago"). */}
        <span className="font-semibold text-fg">{actionLabel}</span>{' '}
        {ago === 'just now' ? ago : `${ago} ago`}.
      </p>
      <div className="flex gap-2">
        <button
          type="button"
          onClick={onConfirm}
          data-testid="tracker-repeat-confirm-yes"
          className="h-10 flex-1 rounded-md bg-inverse text-sm font-semibold text-inverse-fg"
        >
          Log it anyway
        </button>
        <button
          type="button"
          onClick={onCancel}
          data-testid="tracker-repeat-confirm-no"
          className="h-10 flex-1 rounded-md border border-line-strong text-sm font-semibold text-fg-2"
        >
          Never mind
        </button>
      </div>
    </div>
  );
}
